import * as React from "react"
import { trpc } from "@/lib/trpc"
import { toast } from "@/components/ui/toast"
import { cn } from "@/lib/utils"

// ── Custom boxy SVG icons (no rounded corners, no lucide) ─────────────────────

const IcoChevron = ( { open }: { open: boolean } ) => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        style={{ transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        aria-hidden="true"
    >
        <path
            d="M1.5 3.5L5 7L8.5 3.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="square"
            strokeLinejoin="miter"
        />
    </svg>
)

const IcoBookmark = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path
            d="M2 1V11L6 8L10 11V1H2Z"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="miter"
        />
    </svg>
)

const IcoX = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path
            d="M1.5 1.5L8.5 8.5M8.5 1.5L1.5 8.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="square"
        />
    </svg>
)

const IcoCheck = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path
            d="M1.5 5L4 7.5L8.5 2.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="square"
            strokeLinejoin="miter"
        />
    </svg>
)

// ── Types ─────────────────────────────────────────────────────────────────────

interface TemplateItem {
    id: string
    value: string
    type: string
    workspaceId: string
    createdAt: string
}

interface TemplateInputProps {
    type: "request_title" | "document_name"
    value: string
    onChange: ( value: string ) => void
    placeholder?: string
    className?: string
    id?: string
}

// ── Fuzzy match — returns true if query is a substring match on the item ──────

function matchesQuery( item: string, query: string ): boolean {
    if ( !query.trim() ) return false
    return item.toLowerCase().includes( query.trim().toLowerCase() )
}

// ── TemplateInput ─────────────────────────────────────────────────────────────

export function TemplateInput( {
    type,
    value,
    onChange,
    placeholder,
    className,
    id,
}: TemplateInputProps ) {
    const inputRef = React.useRef<HTMLInputElement>( null )
    const dropdownRef = React.useRef<HTMLDivElement>( null )
    const containerRef = React.useRef<HTMLDivElement>( null )

    const [open, setOpen] = React.useState( false )
    const [activeIndex, setActiveIndex] = React.useState( -1 )

    const utils = trpc.useUtils()
    const templates = trpc.reusableFields.list.useQuery( { type } )
    const items: TemplateItem[] = templates.data ?? []

    // Which items to show in the dropdown:
    //   • Arrow button opened it with no query → show all
    //   • User is typing → show only fuzzy matches
    const [showAll, setShowAll] = React.useState( false )

    const visibleItems = React.useMemo( () => {
        if ( showAll || !value.trim() ) return items
        return items.filter( item => matchesQuery( item.value, value ) )
    }, [items, value, showAll] )

    const isAlreadySaved = items.some(
        item => item.value.trim().toLowerCase() === value.trim().toLowerCase()
    )

    // ── Mutations ───────────────────────────────────────────────────────────────

    const createTemplate = trpc.reusableFields.create.useMutation( {
        async onMutate( newItem ) {
            await utils.reusableFields.list.cancel( { type } )
            const prev = utils.reusableFields.list.getData( { type } )
            utils.reusableFields.list.setData( { type }, old => [
                ...( old ?? [] ),
                {
                    id: "temp-" + Date.now(),
                    value: newItem.value,
                    type: newItem.type,
                    workspaceId: "",
                    createdAt: new Date().toISOString(),
                },
            ] )
            return { prev }
        },
        onError( _, __, ctx ) {
            utils.reusableFields.list.setData( { type }, ctx?.prev )
            toast.error( "Failed to save template" )
        },
        onSettled() {
            utils.reusableFields.list.invalidate( { type } )
        },
        onSuccess() {
            toast.success( "Template saved" )
        },
    } )

    const deleteTemplate = trpc.reusableFields.delete.useMutation( {
        async onMutate( { id: delId } ) {
            await utils.reusableFields.list.cancel( { type } )
            const prev = utils.reusableFields.list.getData( { type } )
            utils.reusableFields.list.setData( { type }, old =>
                ( old ?? [] ).filter( t => t.id !== delId )
            )
            return { prev }
        },
        onError( _, __, ctx ) {
            utils.reusableFields.list.setData( { type }, ctx?.prev )
            toast.error( "Failed to delete template" )
        },
        onSettled() {
            utils.reusableFields.list.invalidate( { type } )
        },
        onSuccess() {
            toast.success( "Template removed" )
        },
    } )

    // ── Open / close logic ──────────────────────────────────────────────────────

    function openDropdown( all = false ) {
        setShowAll( all )
        setActiveIndex( -1 )
        setOpen( true )
    }

    function closeDropdown() {
        setOpen( false )
        setActiveIndex( -1 )
    }

    // Auto-open when typing matches saved items
    React.useEffect( () => {
        if ( !open && value.trim() && items.some( i => matchesQuery( i.value, value ) ) ) {
            setShowAll( false )
            setOpen( true )
            setActiveIndex( -1 )
        }
        if ( open && !showAll && value.trim() && visibleItems.length === 0 ) {
            closeDropdown()
        }
    }, [value, items] ) // eslint-disable-line

    // Click-outside to close
    React.useEffect( () => {
        function handlePointer( e: PointerEvent ) {
            if (
                containerRef.current &&
                !containerRef.current.contains( e.target as Node )
            ) {
                closeDropdown()
            }
        }
        document.addEventListener( "pointerdown", handlePointer )
        return () => document.removeEventListener( "pointerdown", handlePointer )
    }, [] )

    // ── Keyboard navigation ─────────────────────────────────────────────────────

    function handleKeyDown( e: React.KeyboardEvent<HTMLInputElement> ) {
        if ( !open ) {
            if ( e.key === "ArrowDown" ) {
                e.preventDefault()
                openDropdown( true )
            }
            return
        }

        switch ( e.key ) {
            case "ArrowDown":
                e.preventDefault()
                setActiveIndex( i => Math.min( i + 1, visibleItems.length - 1 ) )
                break
            case "ArrowUp":
                e.preventDefault()
                setActiveIndex( i => Math.max( i - 1, -1 ) )
                break
            case "Enter":
                e.preventDefault()
                if ( activeIndex >= 0 && visibleItems[activeIndex] ) {
                    selectItem( visibleItems[activeIndex].value )
                }
                break
            case "Escape":
                e.preventDefault()
                closeDropdown()
                inputRef.current?.focus()
                break
            case "Tab":
                closeDropdown()
                break
        }
    }

    // ── Select an item ──────────────────────────────────────────────────────────

    function selectItem( val: string ) {
        onChange( val )
        closeDropdown()
        // Restore focus to input after a frame so the value is applied first
        requestAnimationFrame( () => inputRef.current?.focus() )
    }

    // ── Save current value as template ──────────────────────────────────────────

    function handleSave( e: React.MouseEvent ) {
        e.preventDefault()
        e.stopPropagation()
        if ( !value.trim() || isAlreadySaved ) return
        createTemplate.mutate( { type, value: value.trim() } )
    }

    // ── Delete a template ───────────────────────────────────────────────────────

    function handleDelete( e: React.MouseEvent | React.TouchEvent, itemId: string ) {
        e.preventDefault()
        e.stopPropagation()
        deleteTemplate.mutate( { id: itemId } )
    }

    // ── Arrow-button: toggle dropdown showing all items ─────────────────────────

    function handleArrowClick( e: React.MouseEvent ) {
        e.preventDefault()
        e.stopPropagation()
        if ( open ) {
            closeDropdown()
        } else {
            openDropdown( true )
        }
        inputRef.current?.focus()
    }

    // ── Render ──────────────────────────────────────────────────────────────────

    return (
        <div
            ref={containerRef}
            className="relative w-full"
            style={{ isolation: "isolate" }}
        >
            {/* ── Input row ─────────────────────────────────────────────────────── */}
            <div
                className={cn(
                    "flex items-center border border-(--border) bg-card transition-colors",
                    "focus-within:border-(--accent)",
                    className
                )}
            >
                {/* Text input */}
                <input
                    ref={inputRef}
                    id={id}
                    type="text"
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    aria-activedescendant={activeIndex >= 0 ? `fc-item-${ activeIndex }` : undefined}
                    aria-controls="fc-dropdown"
                    value={value}
                    onChange={e => onChange( e.target.value )}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoComplete="off"
                    spellCheck={false}
                    className={cn(
                        "flex-1 h-9 px-3 bg-transparent text-[12px] text-foreground",
                        "placeholder:text-muted-foreground outline-none min-w-0"
                    )}
                />

                {/* Action buttons */}
                <div className="flex items-center shrink-0 border-l border-(--border)">
                    {/* Save bookmark — only when value is unsaved */}
                    {value.trim() && !isAlreadySaved && (
                        <button
                            type="button"
                            title="Save as template"
                            onClick={handleSave}
                            disabled={createTemplate.isPending}
                            className={cn(
                                "flex items-center justify-center h-9 w-8",
                                "text-muted-foreground hover:text-(--accent) hover:bg-(--accent-bg)",
                                "transition-colors disabled:opacity-40 border-r border-(--border)"
                            )}
                        >
                            <IcoBookmark />
                        </button>
                    )}

                    {/* Arrow toggle */}
                    <button
                        type="button"
                        title={open ? "Close suggestions" : "Show saved templates"}
                        aria-label={open ? "Close suggestions" : "Show saved templates"}
                        // Use onPointerDown so it fires before blur on the input
                        onPointerDown={e => {
                            e.preventDefault()   // prevent input blur
                            if ( open ) {
                                closeDropdown()
                            } else {
                                openDropdown( true )
                                inputRef.current?.focus()
                            }
                        }}
                        className={cn(
                            "flex items-center justify-center h-9 w-8",
                            "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                            "transition-colors",
                            open && "text-(--accent) bg-(--accent-bg)"
                        )}
                    >
                        <IcoChevron open={open} />
                    </button>
                </div>
            </div>

            {/* ── Dropdown ──────────────────────────────────────────────────────── */}
            {open && (
                <div
                    ref={dropdownRef}
                    id="fc-dropdown"
                    role="listbox"
                    aria-label="Saved templates"
                    className={cn(
                        "absolute top-full left-0 right-0 z-50 mt-px",
                        "bg-card border border-(--border)",
                        "shadow-[0_4px_16px_rgba(28,25,23,.10)]",
                        "max-h-[220px] overflow-y-auto",
                        "animate-in fade-in-0 slide-in-from-top-1 duration-100"
                    )}
                >
                    {visibleItems.length === 0 ? (
                        <div className="flex items-center gap-2 px-3 py-3 text-[11px] text-muted-foreground">
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                                <path d="M2 1V11L6 8L10 11V1H2Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="miter" />
                            </svg>
                            {value.trim()
                                ? "No matching templates — type more or save this one"
                                : "No saved templates yet"}
                        </div>
                    ) : (
                        <>
                            {/* List header */}
                            <div className="flex items-center justify-between px-3 py-2 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
                                <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                                    Saved templates
                                </p>
                                <p className="text-[9.5px] text-muted-foreground/60">
                                    {visibleItems.length}
                                </p>
                            </div>

                            {/* Items */}
                            {visibleItems.map( ( item, index ) => {
                                const isActive = index === activeIndex
                                const isSelected = item.value.toLowerCase() === value.toLowerCase()

                                return (
                                    <div
                                        key={item.id}
                                        id={`fc-item-${ index }`}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={cn(
                                            "group flex items-center justify-between gap-2",
                                            "px-3 py-2.5 cursor-pointer select-none",
                                            "border-b border-(--border) last:border-b-0",
                                            "transition-colors",
                                            isActive && "bg-(--brand-dim) text-(--brand)",
                                            isSelected && !isActive && "bg-(--green-bg)",
                                            !isActive && !isSelected && "hover:bg-(--surface-hover)"
                                        )}
                                        // onPointerDown fires on both mouse and touch BEFORE blur
                                        onPointerDown={e => {
                                            e.preventDefault()   // critical — prevents input blur
                                            selectItem( item.value )
                                        }}
                                    >
                                        {/* Label + selected check */}
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            {isSelected ? (
                                                <span className="shrink-0 text-(--green)">
                                                    <IcoCheck />
                                                </span>
                                            ) : (
                                                <span className="shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors">
                                                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                                                        <path d="M2 1V11L6 8L10 11V1H2Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="miter" />
                                                    </svg>
                                                </span>
                                            )}
                                            <span
                                                className={cn(
                                                    "text-[12px] font-medium truncate",
                                                    isSelected ? "text-(--green) font-semibold"
                                                        : isActive ? "text-(--brand) font-semibold"
                                                            : "text-foreground"
                                                )}
                                            >
                                                {item.value}
                                            </span>
                                        </div>

                                        {/* Delete button */}
                                        <button
                                            type="button"
                                            aria-label={`Remove "${ item.value }" from saved templates`}
                                            title="Remove template"
                                            className={cn(
                                                "flex items-center justify-center",
                                                "w-6 h-6 shrink-0",
                                                "text-muted-foreground/40",
                                                "hover:text-(--warn) hover:bg-(--colors-red-100)",
                                                "transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                                            )}
                                            // onPointerDown with stopPropagation so row selection doesn't fire
                                            onPointerDown={e => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                handleDelete( e, item.id )
                                            }}
                                        >
                                            <IcoX />
                                        </button>
                                    </div>
                                )
                            } )}
                        </>
                    )}

                    {/* Footer hint */}
                    <div className="px-3 py-2 border-t border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
                        <p className="text-[9px] text-muted-foreground/50 select-none">
                            ↑ ↓ navigate · Enter to select · Esc to close
                            {value.trim() && !isAlreadySaved && " · bookmark icon to save current"}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}